import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getTableById, createTable, updateTable, getSections } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import FloatingInput from "../components/UI/FloatingInput";
import SelectBox from "../components/UI/SelectBox";
import ToggleSwitch from "../components/UI/ToggleSwitch";
import { notify } from "../utils/notify";
import PageHeader from "../components/UI/PageHeader";
import { useTranslation } from "../context/I18nContext";

export default function AddEditTable() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const { t } = useTranslation("tables");
  const { t: tCommon } = useTranslation("common");

  const [sections, setSections] = useState([]);
  const [form, setForm] = useState({
    section_id: "",
    name: "",
    capacity: "",
    is_active: true,
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    getSections(token).then(setSections).catch(() => notify.error(t("notify_load_sections_failed", "Failed to load sections")));
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (id) {
      getTableById(token, id).then((tbl) => {
        setForm({
          section_id: tbl.section_id ? String(tbl.section_id) : "",
          name: tbl.name || "",
          capacity: tbl.capacity ? String(tbl.capacity) : "",
          is_active: tbl.is_active !== undefined ? tbl.is_active : true,
        });
      }).catch(() => notify.error(t("notify_load_failed", "Failed to load table")));
    }
  }, [id, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Each option's label carries its own branch name too — a table's most
  // important context is WHICH branch it's in, and Section alone
  // ("Main Hall") reads ambiguously once more than one branch has a
  // section with that same common name.
  const sectionOptions = sections.map((s) => ({ value: String(s.id), label: `${s.name} (${s.branch?.name || `#${s.branch_id}`})` }));

  const setField = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleSubmit = async () => {
    setErrors({});
    const newErrors = {};
    if (!form.section_id) newErrors.section_id = t("validation_section_required", "Section is required");
    if (!form.name.trim()) newErrors.name = t("validation_name_required", "Table name is required");

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      notify.error(tCommon("validation_fill_required_fields", "Please fill in the required fields correctly"));
      return;
    }

    setLoading(true);
    const payload = {
      section_id: Number(form.section_id),
      name: form.name.trim(),
      capacity: form.capacity ? Number(form.capacity) : 0,
      is_active: form.is_active,
    };

    try {
      if (id) {
        await updateTable(token, id, payload);
        notify.success(t("notify_updated", "Table updated successfully"));
      } else {
        await createTable(token, payload);
        notify.success(t("notify_created", "Table created successfully"));
      }
      navigate("/tables");
    } catch (err) {
      notify.error(err.data?.error || err.message || tCommon("error_generic", "Something went wrong"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto">
      <PageHeader className="flex items-center gap-4 mb-8 border-b border-gray-100 pb-6">
        <button onClick={() => navigate("/tables")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
          <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
        </button>
        <div>
          <h2 className="text-2xl font-black text-gray-800 tracking-tight">{id ? t("edit_title", "Edit Table") : t("add_title", "Add New Table")}</h2>
          <p className="text-gray-400 text-sm italic font-medium">{t("form_subtitle", "A physical table — pick which section it belongs to")}</p>
        </div>
      </PageHeader>

      <div className="bg-white p-6 md:p-8 rounded-[2.5rem] border border-gray-100 shadow-sm">
        <div className="space-y-6">
          <SelectBox label={t("field_section_label", "Section")} options={sectionOptions} value={form.section_id} onChange={(val) => setForm((prev) => ({ ...prev, section_id: val }))} error={errors.section_id} icon={ICONS.Sections} />
          <FloatingInput label={t("field_name_label", "Table Name")} value={form.name} onChange={setField("name")} error={errors.name} icon={ICONS.Tables} />
          <FloatingInput label={t("field_capacity_label", "Capacity (optional)")} type="number" min="0" value={form.capacity} onChange={setField("capacity")} />

          <ToggleSwitch
            label={tCommon("status_active", "Active")}
            checked={form.is_active}
            onChange={(val) => setForm((prev) => ({ ...prev, is_active: val }))}
          />

          <div className="flex gap-4 pt-6 border-t border-gray-50">
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={loading}
              className="flex-grow shadow-lg shadow-blue-500/10"
              icon={id ? ICONS.Save : ICONS.Plus}
            >
              {id ? t("btn_update_table", "Update Table") : t("btn_create_table", "Create Table")}
            </Button>
            <Button variant="secondary" onClick={() => navigate("/tables")}>{tCommon("btn_cancel", "Cancel")}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
